import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CalendarDays, Globe } from "lucide-react";
import api from "@/lib/api";
import useGeoCurrency, { priceLabel } from "@/lib/useGeoCurrency";
import { optimizeImageUrl } from "@/lib/imageUtils";
import PageBanner from "@/components/PageBanner";

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const currency = useGeoCurrency();
  const [pageEnabled, setPageEnabled] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/packages").then((res) => setPackages(res.data)),
      api.get("/page-visibility").then((res) => setPageEnabled(res.data.packages)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (!loading && !pageEnabled) {
    return (
      <div data-testid="packages-page-disabled" className="min-h-[60vh] flex items-center justify-center text-[var(--brand-secondary)]">
        This page is currently unavailable.
      </div>
    );
  }

  return (
    <div data-testid="packages-list-page" className="min-h-[70vh] bg-[#FDFCFB]">
      <PageBanner pageKey="packages" eyebrow="Sacred Journeys & Getaways" title="Umrah &amp; Tour Packages" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-10 sm:pt-0">
        <p data-testid="packages-seo-intro" className="text-center text-sm text-[var(--brand-secondary)] max-w-3xl mx-auto mb-4 leading-relaxed">
          Gulfudaan offers carefully planned Umrah packages and holiday tour packages from India covering flights, hotels near the Haram, ground transport, visa processing and on-ground assistance. Every package below shows the full itinerary, inclusions and transparent pricing so you can book your sacred journey or getaway with complete confidence.
        </p>
        <p data-testid="packages-availability-note" className="text-center text-xs text-[var(--brand-secondary)] mb-8 flex items-center justify-center gap-1.5">
          <Globe size={13} className="text-[var(--brand-primary)]" /> Currently available for customers in <strong className="text-[#1A1A1A]">India</strong>
        </p>
        {loading ? (
          <div className="flex justify-center"><Loader2 className="animate-spin text-[var(--brand-primary)]" size={28} /></div>
        ) : packages.length === 0 ? (
          <p data-testid="packages-empty-state" className="text-center text-[var(--brand-secondary)] py-10">No packages available right now. Please check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((p) => (
              <Link key={p.slug} to={`/packages/${p.slug}`} data-testid={`package-card-${p.slug}`}
                className="rounded-lg border border-[#E5E1DA] bg-white overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="relative">
                  <img src={optimizeImageUrl(p.banner_image, 500)} alt={p.title} loading="lazy" className="w-full h-48 object-cover" />
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wide font-bold bg-[var(--brand-primary)] text-white px-2.5 py-1 rounded-full">{p.package_type}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl text-[#1A1A1A] mb-2 line-clamp-2">{p.title}</h3>
                  <p className="text-sm text-[var(--brand-secondary)] line-clamp-2 mb-4">{p.short_description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[var(--brand-secondary)] flex items-center gap-1"><CalendarDays size={13} /> {p.duration_days} Days</span>
                    <span className="font-price text-lg text-[#1A1A1A]">{priceLabel(p.price_inr, p.price_usd, currency)}</span>
                  </div>
                  <span className="text-[var(--brand-primary)] text-sm font-medium flex items-center gap-1 mt-4">View Details <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
